import { CheckCircle2, AlertCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, Copy, Check, Download } from 'lucide-react'
import React from 'react'
import QRCode from 'react-qr-code'
import type { VerificationResult, SignatureStatus } from '@/shared/types/api.types'
import { SIGNATURE_STATUS_CONFIG, getVerificationUrl } from '@/shared/lib/constants'
import { formatDate } from '@/shared/lib/utils'
import { cn } from '@/shared/lib/utils'

interface ResultPanelProps {
  result: VerificationResult
  onReset: () => void
}

const STATUS_ICONS: Record<SignatureStatus, React.ElementType> = {
  FIRMA_DIGITAL_VALIDA: CheckCircle2,
  FIRMA_ELECTRONICA_DETECTADA: AlertCircle,
  SIN_FIRMA_VALIDA: XCircle,
  ERROR: AlertTriangle,
}

export function ResultPanel({ result, onReset }: ResultPanelProps) {
  const config = SIGNATURE_STATUS_CONFIG[result.status]
  const Icon = STATUS_ICONS[result.status]
  const [showDetailedView, setShowDetailedView] = React.useState(false)
  const [copiedUrl, setCopiedUrl] = React.useState(false)

  // Generate verification URL based on current environment
  const verificationUrl = result.metadata?.verification_id
    ? getVerificationUrl(result.metadata.verification_id)
    : ''

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDownloadPDF = () => {
    if (!result.report_pdf_base64) return

    // Convert base64 to blob
    const byteCharacters = atob(result.report_pdf_base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: 'application/pdf' })

    // Create download link
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // Use filename from backend, or fallback to generic name
    link.download = result.report_filename || `constancia_${new Date().toISOString().slice(0, 10)}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadQR = () => {
    if (!result.qr_code_base64) return

    // Convert base64 to blob
    const byteCharacters = atob(result.qr_code_base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: 'image/png' })

    // Create download link
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qr_verificación_${result.metadata?.verification_id || 'signwiz'}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Main Result Card */}
      <div
        className={cn(
          'card border-2',
          config.borderColor,
          config.bgColor
        )}
      >
        <div className="flex items-start gap-4">
          <Icon className={cn('w-12 h-12 flex-shrink-0', config.textColor)} />

          <div className="flex-1">
            <h2 className={cn('text-2xl font-bold mb-2', config.textColor)}>
              {config.label}
            </h2>

            {/* Filename */}
            {result.original_filename && (
              <p className="text-legal-600 dark:text-legal-400 text-sm mb-2 font-mono">
                Archivo: {result.original_filename}
              </p>
            )}

            <p className="text-legal-700 dark:text-legal-300 text-lg mb-4">{result.message}</p>

            {/* Legal Implication */}
            <div className="p-4 bg-white/50 dark:bg-legal-700/50 rounded-lg border border-legal-200 dark:border-legal-600">
              <h3 className="font-semibold text-legal-900 dark:text-white mb-2">
                Implicancia Legal (Argentina - Ley 25.506)
              </h3>
              <p className="text-legal-700 dark:text-legal-300">{result.legal_implication}</p>
            </div>

            {/* Toggle Detailed View */}
            <button
              onClick={() => setShowDetailedView(!showDetailedView)}
              className="mt-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {showDetailedView ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Ocultar información detallada
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Ver información detallada del certificado
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Information (collapsible) */}
      {showDetailedView && (
        <>
      {/* Regulatory Framework (if present) */}
      {result.regulatory_framework && (
        <div className="card border-l-4 border-blue-500 dark:border-blue-600">
          <h3 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
            Marco Regulatorio
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Jurisdicción</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-semibold">
                {result.regulatory_framework.name}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Descripción</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {result.regulatory_framework.description}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Validez Legal</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {result.regulatory_framework.validity}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Referencia Legal</dt>
              <dd className="mt-1">
                <a
                  href={result.regulatory_framework.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                >
                  Ver normativa completa →
                </a>
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Certificate Details (if present) */}
      {result.certificate && (
        <div className="card">
          <h3 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
            Detalles del Certificado
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Sujeto</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-sm">
                {result.certificate.subject}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Emisor</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-sm">
                {result.certificate.issuer}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                Válido desde
              </dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {formatDate(result.certificate.valid_from)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                Válido hasta
              </dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {formatDate(result.certificate.valid_to)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                Número de Serie
              </dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-sm">
                {result.certificate.serial_number}
              </dd>
            </div>
            {result.signature_timestamp && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                  Fecha de Firma
                </dt>
                <dd className="mt-1 text-legal-900 dark:text-legal-100">
                  {formatDate(result.signature_timestamp)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Hash Information */}
      {result.hash && (
        <div className="card">
          <h3 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
            Información de Hash
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Algoritmo</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono">
                {result.hash.algorithm}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Hash</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-xs break-all bg-legal-100 dark:bg-legal-700 p-3 rounded">
                {result.hash.value}
              </dd>
            </div>
            {result.hash.matches_local !== undefined && (
              <div>
                <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">
                  Coincidencia con hash local
                </dt>
                <dd className="mt-1">
                  {result.hash.matches_local ? (
                    <span className="badge-success">✓ Coincide</span>
                  ) : (
                    <span className="badge-error">✗ No coincide</span>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Validation Metadata (matching PDF order) */}
      {result.metadata && (
        <div className="card border-l-4 border-cyan-500 dark:border-cyan-600">
          <h3 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
            Validación de Constancia
          </h3>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">ID de Verificación</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono text-sm break-all bg-legal-100 dark:bg-legal-700 p-2 rounded">
                {result.metadata.verification_id}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Fecha/Hora de Verificación</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100">
                {formatDate(result.metadata.verification_timestamp)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400">Versión SignWiz</dt>
              <dd className="mt-1 text-legal-900 dark:text-legal-100 font-mono">
                {result.metadata.signwiz_version}
              </dd>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center py-4 bg-white dark:bg-legal-800 rounded-lg border-2 border-dashed border-legal-300 dark:border-legal-600">
              <p className="text-sm font-medium text-legal-500 dark:text-legal-400 mb-3">
                Escanea para validar públicamente
              </p>
              <div className="bg-white p-3 sm:p-4 rounded-lg w-[140px] sm:w-[180px]">
                <QRCode
                  value={verificationUrl}
                  style={{ width: '100%', height: 'auto' }}
                  level="M"
                />
              </div>
              {result.qr_code_base64 && (
                <button
                  onClick={handleDownloadQR}
                  className="mt-3 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Download className="w-4 h-4" />
                  Descargar QR
                </button>
              )}
            </div>

            {/* URL with copy button */}
            <div>
              <dt className="text-sm font-medium text-legal-500 dark:text-legal-400 mb-2">URL de Validación Pública</dt>
              <dd className="flex items-center gap-2">
                <a
                  href={verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm break-all flex-1"
                >
                  {verificationUrl}
                </a>
                <button
                  onClick={() => handleCopyUrl(verificationUrl)}
                  className="btn-secondary p-2 flex-shrink-0"
                  title="Copiar URL"
                >
                  {copiedUrl ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </dd>
              <p className="text-xs text-legal-500 dark:text-legal-400 mt-2">
                El código QR y esta URL permiten validar la constancia públicamente
              </p>
            </div>
          </dl>
        </div>
      )}
        </>
      )}

      {/* Actions */}
      <div className="flex gap-4">
        {result.report_pdf_base64 && (
          <button
            onClick={handleDownloadPDF}
            className="btn-primary flex-1"
          >
            Descargar Constancia PDF
          </button>
        )}
        <button onClick={onReset} className="btn-secondary">
          Verificar Otro Documento
        </button>
      </div>

      {/* Timestamp */}
      <p className="text-center text-sm text-legal-500 dark:text-legal-400">
        Verificado el {formatDate(result.timestamp)}
      </p>
    </div>
  )
}
